// shared/status-rune.ts — render NON-STANDARD status effects as a runic sigil instead of "?".
//
// A "standard" status effect (Prone, Stunned, …) has a bespoke PNG registered in the icon registry by
// its catalog UUID, so iconMap() resolves it. A status applied FROM an ability / spell / curse has no
// registered icon, so iconMap() falls back to the help-circle "?" placeholder. For those, render the
// status's resolved def as the lowest-detail (L0) rune sigil — far more legible than "?", and it rhymes
// with the rune medallions used elsewhere (ability bar, object cards, spell/menta lists).
//
// Shared by the PC-sheet summary bar (player-character-sheet/tabs/summary.ts) and the encounter-creature
// console (info-panel/creature-console.ts) so both surfaces behave identically. The emitted
// .ft-rune-mount placeholder is hydrated by the same window.ftMountRuneIcons() footer scanner the rest of
// the rune surfaces use (template_page_footer.php) — the caller MUST invoke it after injecting the HTML.

interface StatusDef {
    items?: unknown;
    item_layer_types?: unknown;
    catalog?: unknown;
}

/** The front-end icon registry (dist/icon-lookup.js → window.FT_ICON_REGISTRY, aliased ICON_LOOKUP). */
function iconRegistry(): Record<string, unknown> | null {
    const w = window as unknown as { FT_ICON_REGISTRY?: Record<string, unknown>; ICON_LOOKUP?: Record<string, unknown> };
    return w.FT_ICON_REGISTRY || w.ICON_LOOKUP || null;
}

/**
 * Mirror of iconMap()'s registry resolution (incl. its rtrim-'s' singular fallback): true when `id`
 * resolves to a real registered icon, false when iconMap() would fall back to help-circle-outline ("?").
 * When the registry isn't loaded yet, returns true (don't force a rune — let iconMap handle it).
 */
export function statusIconRegistered(id: string): boolean {
    if (!id) return false;
    const reg = iconRegistry();
    if (!reg) return true;
    if (id in reg) return true;
    if (id.endsWith('s')) {
        const singular = id.replace(/s+$/, ''); // rtrim-equivalent, matches core-helpers.js iconMap()
        if (singular && singular !== id && singular in reg) return true;
    }
    return false;
}

/**
 * Should this status render as a rune? Only when (a) it has NO registered icon (would be "?") AND (b) its
 * resolved def actually carries rune-able layer data (item_layer_types). Otherwise the caller falls back
 * to iconMap() — for a registered status that keeps its bespoke PNG, and for a bare identity shell (no
 * layers to draw) that stays the "?" rather than an empty sigil.
 */
export function statusRunes(id: string, def: unknown): boolean {
    if (statusIconRegistered(id)) return false;
    const d = def as StatusDef | null | undefined;
    const ilt = d && typeof d === 'object' ? d.item_layer_types : null;
    return !!(ilt && typeof ilt === 'object' && !Array.isArray(ilt) && Object.keys(ilt).length > 0);
}

/**
 * Inline rune mount carrying the status def's {items, item_layer_types[, catalog]}. The footer scanner
 * derives the sigil LOD from data-rune-size (>=96→L2, >=72→L1, else L0). Defaults — size 25 (LOD 0) +
 * class `ft-rune-status` — preserve the original PC-sheet / creature-console inline behaviour; callers
 * that want a richer face pass their own (the combat log uses size 72 → LOD 1 in a 64px `.ft-rune-log`
 * box). The def's OWN catalog is shipped only when it carries a non-empty one (so reference layers
 * resolve to item_names for the name-keyed icon files); otherwise `catalog` is omitted so the footer
 * scanner keeps the globally-set caster catalog (RuneIcon.setCatalog) — shipping an empty {} would WIPE
 * it (the scanner only overrides on a non-empty catalog).
 */
export function statusRuneMountHtml(def: StatusDef, opts: { size?: number; className?: string } = {}): string {
    const size = opts.size ?? 25;
    const cls = opts.className ?? 'ft-rune-status';
    const payload: { items: unknown; item_layer_types: unknown; catalog?: unknown } = {
        items: def?.items ?? {},
        item_layer_types: def?.item_layer_types ?? {},
    };
    const cat = def?.catalog;
    if (cat && typeof cat === 'object' && Object.keys(cat as object).length) payload.catalog = cat;

    let json = 'null';
    try { json = JSON.stringify(payload); } catch { json = 'null'; }
    json = json.replace(/<\//g, '<\\/'); // defang any "</script>" so it can't close the tag early

    return '<div class="ft-rune-mount ' + cls + '" data-rune-size="' + size + '">'
        + '<script type="application/json" class="ft-rune-blob">' + json + '</script>'
        + '</div>';
}
